/**
 * MentorBroadcastScreen.js
 *
 * Broadcaster screen for Mentors.
 * Currently a placeholder directing mentors to use the web studio for high-quality Icecast broadcasting,
 * or as a landing page to start/stop the broadcast session on the server.
 */
import React, { useState, useEffect, useContext } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  SafeAreaView,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Clipboard,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { AuthContext } from '../../contexts/AuthContext';

const API_BASE = 'https://yc.intuitlms.online'; // production

const MentorBroadcastScreen = () => {
  const navigation = useNavigation();
  const { user } = useContext(AuthContext);

  const [loading, setLoading]       = useState(false);
  const [activeSession, setActiveSession] = useState(null);

  // ── Session Management ──────────────────────────────────────────────────────

  const startBroadcast = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/radio/stream/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          mountPoint: `/yc-mentor-${user.id}`,
          title: `${user.name}'s Live Stream`,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start broadcast');

      setActiveSession(data.session);
      Alert.alert(
        'Broadcast Session Started!',
        'Your mount point is now active. Please use your preferred Icecast source software (e.g. BUTT, Larix, or Mixxx) to start streaming audio.'
      );
    } catch (err) {
      Alert.alert('Error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const stopBroadcast = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/radio/stream/end`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.token}` },
      });

      if (!res.ok) throw new Error('Failed to end broadcast');

      setActiveSession(null);
      Alert.alert('Success', 'Broadcast session closed.');
    } catch (err) {
      Alert.alert('Error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    Clipboard.setString(text);
    Alert.alert('Copied', 'URL copied to clipboard');
  };

  return (
    <SafeAreaView style={styles.screen}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Broadcaster Studio</Text>
        <View style={{ width: 40 }} />
      </View>

      <View style={styles.content}>
        <MaterialCommunityIcons name="microphone-variant" size={64} color={activeSession ? "#FF4757" : "#6366F1"} style={styles.icon} />

        <Text style={styles.title}>
          {activeSession ? 'Your Broadcast is Registered' : 'Start a New Broadcast'}
        </Text>
        <Text style={styles.subtitle}>
          {activeSession
            ? 'Connect your Icecast encoder to begin streaming live audio.'
            : 'Register a mount point on the server to start your live radio session.'}
        </Text>

        {activeSession ? (
          <View style={styles.sessionBox}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Server URL:</Text>
              <Text style={styles.infoValue} numberOfLines={1}>{activeSession.icecastSourceUrl}</Text>
              <TouchableOpacity onPress={() => copyToClipboard(activeSession.icecastSourceUrl)}>
                <Ionicons name="copy-outline" size={18} color="#6366F1" style={{ marginLeft: 8 }} />
              </TouchableOpacity>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Mount Point:</Text>
              <Text style={styles.infoValue}>{activeSession.mountPoint}</Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Password:</Text>
              <Text style={styles.infoValue}>Hidden</Text>
              <TouchableOpacity onPress={() => Alert.alert('Password', activeSession.sourcePassword)}>
                <Ionicons name="eye-outline" size={18} color="#6366F1" style={{ marginLeft: 8 }} />
              </TouchableOpacity>
            </View>

            <TouchableOpacity style={styles.stopBtn} onPress={stopBroadcast} disabled={loading}>
              {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.btnText}>Stop Session</Text>}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity style={styles.startBtn} onPress={startBroadcast} disabled={loading}>
            {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.btnText}>Register Session</Text>}
          </TouchableOpacity>
        )}

        <View style={styles.helpBox}>
          <Ionicons name="information-circle-outline" size={20} color="#94A3B8" />
          <Text style={styles.helpText}>
            For a seamless mobile broadcasting experience, we recommend using Larix Broadcaster or BUTT (on PC) pointing to the settings above.
          </Text>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#0B0B12' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  backBtn: { padding: 8 },
  content: { flex: 1, padding: 24, alignItems: 'center', justifyContent: 'center' },
  icon: { marginBottom: 20 },
  title: { color: '#fff', fontSize: 22, fontWeight: '800', textAlign: 'center', marginBottom: 12 },
  subtitle: { color: 'rgba(255,255,255,0.5)', fontSize: 14, textAlign: 'center', marginBottom: 32, lineHeight: 20 },
  startBtn: {
    backgroundColor: '#6366F1',
    paddingHorizontal: 40,
    paddingVertical: 14,
    borderRadius: 12,
    minWidth: 200,
    alignItems: 'center',
  },
  stopBtn: {
    backgroundColor: '#FF4757',
    paddingHorizontal: 40,
    paddingVertical: 14,
    borderRadius: 12,
    marginTop: 20,
    alignItems: 'center',
  },
  btnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  sessionBox: {
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(99,102,241,0.3)',
  },
  infoRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  infoLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 12, width: 90 },
  infoValue: { color: '#fff', fontSize: 13, flex: 1, fontWeight: '500' },
  helpBox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 40,
    backgroundColor: 'rgba(99,102,241,0.05)',
    padding: 12,
    borderRadius: 8,
    gap: 10,
  },
  helpText: { color: '#94A3B8', fontSize: 12, flex: 1, lineHeight: 18 },
});

export default MentorBroadcastScreen;
